import React, { useEffect } from 'react';
import { Navigate, Outlet, useNavigate } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { RootState } from '../reducers';
import Loader from '../features/app/loader/LoaderFullScreen';
import { getInitialState } from '../features/app/appSlice';
import {
  getToken,
  setToken,
  isTokenExpired,
  removeToken,
  isNewerThanStoredToken,
} from '../lib/localStorageHandler';
import { setHeader } from '../lib/baseAxios';
import { addNotification } from '../features/dashboard/dashboardSlice';
import { useQuery } from '../hooks/urlHooks';
import AccountDeletingCover from '../features/auth/AccountDeletingCover';
import { getParameterFromURLHash } from '../utils/secure';

const PrivateRoute: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const {
    token: tokenFromQuery,
    emailVerification,
    joinCode,
    zoomCode: zoomCodeFromQuery,
    state: stateFromQuery,
    redirectUrl,
    intent,
  } = useQuery();
  const zoomCode = zoomCodeFromQuery || getParameterFromURLHash('code');
  console.log(
    t('authenticatedRoute.zoomCode'),
    zoomCode ? `${zoomCode.slice(0, 1)}******` : zoomCode
  );

  const stateValue = stateFromQuery || getParameterFromURLHash('state');

  const isEmailVerification = !!emailVerification;
  const tokenFromLocalStorage = getToken();
  const token = tokenFromQuery || tokenFromLocalStorage;
  const { app } = useSelector((state: RootState) => state) || {};
  const { status: appStatus } = app;
  const deleteRequestedAt = useSelector(
    (state: RootState) => state.settings.user?.deleteRequestedAt
  );
  const dispatch = useDispatch();

  useEffect(() => {
    // Set token from query only if it's newer than the stored one
    if (tokenFromQuery && isNewerThanStoredToken(tokenFromQuery)) {
      setToken(tokenFromQuery);
    }

    if (appStatus === 'idle' && token) {
      setHeader(token);

      // Only dispatch getInitialState if `joinCode` or `zoomCode` is provided
      if (joinCode || zoomCode) {
        dispatch(getInitialState({ joinCode, zoomCode, state: stateValue }));
      } else {
        dispatch(getInitialState({}));
      }

      if (isEmailVerification) {
        dispatch(
          addNotification({
            id: Date.now().toString(),
            type: 'transcription',
            title: t('authenticatedRoute.emailVerified.title'),
            message: t('authenticatedRoute.emailVerified.message'),
            link: '',
            read: false,
            timestamp: Date.now(),
          })
        );
      }

      // Redirect based on joinCode or zoomCode
      if (joinCode) {
        navigate(`/dashboard?joinCode=${joinCode}`);
      } else if (zoomCode) {
        navigate(`/dashboard?code=${zoomCode}`);
      }
    }
  }, [
    tokenFromQuery,
    appStatus,
    token,
    joinCode,
    zoomCode,
    isEmailVerification,
    dispatch,
    navigate,
    t,
  ]);

  if (!token) {
    const currentLocation = window.location.href;
    const referrerUrl = window.location.hash
      ? window.location.hash.substring(1)
      : currentLocation;
    const queryParams = new URLSearchParams({
      redirectUrl: currentLocation,
      referrerUrl,
      intent: intent || '',
    });
    return <Navigate to={`/signup?${queryParams}`} />;
  }

  // Check if token is expired and remove it if so
  if (isTokenExpired(token)) {
    removeToken();
    const currentLocation = window.location.href;
    const referrerUrl = window.location.hash
      ? window.location.hash.substring(1)
      : currentLocation;
    const queryParams = new URLSearchParams({
      redirectUrl: currentLocation,
      referrerUrl,
      intent: intent || '',
    });
    return <Navigate to={`/signup?${queryParams}`} />;
  }

  if (deleteRequestedAt) {
    return <AccountDeletingCover />;
  }

  const redirectHashUrl = redirectUrl?.includes('#')
    ? redirectUrl.split('#').pop()
    : '';

  if (redirectHashUrl) {
    return <Navigate to={redirectHashUrl} />;
  }

  if (appStatus !== 'succeeded') return <Loader />;

  return <Outlet />;
};

export default PrivateRoute;
